import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { useActiveAlert } from '@/hooks/useAdminApi';
import { Bell, AlertTriangle, Info, CheckCircle, X, Sparkles, Megaphone } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * SiteAlertSystem - Sistema completo per gli avvisi del sito
 *
 * Comportamento:
 * - Floating button (campanella) sempre visibile in basso a destra quando c'è un alert attivo
 * - Modal si apre automaticamente SOLO in homepage alla prima visita
 * - Click sul floating button apre il modal
 * - Click fuori dal modal o sulla X lo chiude
 */
export function SiteAlertSystem() {
    const { data: alert, isLoading } = useActiveAlert();
    const [location] = useLocation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const [showPulse, setShowPulse] = useState(true);

    const isHomePage = location === '/' || location === '';

    // Auto-open solo in homepage e solo se non già dismissato
    useEffect(() => {
        if (alert && alert.isActive && isHomePage && !hasAutoOpened) {
            const dismissedId = localStorage.getItem('dismissed_alert_id');
            if (dismissedId !== alert.id.toString()) {
                const timer = setTimeout(() => {
                    setIsModalOpen(true);
                    setHasAutoOpened(true);
                }, 500); // Delay per UX migliore
                return () => clearTimeout(timer);
            }
        }
    }, [alert, isHomePage, hasAutoOpened]);

    // Stop pulse dopo un po'
    useEffect(() => {
        const timer = setTimeout(() => setShowPulse(false), 10000);
        return () => clearTimeout(timer);
    }, []);

    const handleClose = useCallback(() => {
        if (!alert?.canClose) return;

        setIsExiting(true);
        setTimeout(() => {
            if (alert) {
                localStorage.setItem('dismissed_alert_id', alert.id.toString());
            }
            setIsModalOpen(false);
            setIsExiting(false);
        }, 400);
    }, [alert]);

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
        setShowPulse(false);
    }, []);

    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget && alert?.canClose) {
            handleClose();
        }
    }, [alert?.canClose, handleClose]);

    if (isLoading || !alert || !alert.isActive) return null;

    const alertConfig = {
        warning: {
            icon: AlertTriangle,
            glowColor: 'rgba(245, 158, 11, 0.4)',
            solidColor: 'rgb(245, 158, 11)',
            borderColor: 'border-amber-500/30',
            iconBg: 'bg-amber-500/20',
            iconColor: 'text-amber-400',
            titleColor: 'text-amber-300',
            accentGradient: 'from-amber-500/20 via-orange-500/10 to-transparent',
            buttonBg: 'bg-amber-500/20 hover:bg-amber-500/30',
            buttonBorder: 'border-amber-500/40',
        },
        error: {
            icon: Bell,
            glowColor: 'rgba(239, 68, 68, 0.4)',
            solidColor: 'rgb(239, 68, 68)',
            borderColor: 'border-red-500/30',
            iconBg: 'bg-red-500/20',
            iconColor: 'text-red-400',
            titleColor: 'text-red-300',
            accentGradient: 'from-red-500/20 via-rose-500/10 to-transparent',
            buttonBg: 'bg-red-500/20 hover:bg-red-500/30',
            buttonBorder: 'border-red-500/40',
        },
        success: {
            icon: CheckCircle,
            glowColor: 'rgba(34, 197, 94, 0.4)',
            solidColor: 'rgb(34, 197, 94)',
            borderColor: 'border-emerald-500/30',
            iconBg: 'bg-emerald-500/20',
            iconColor: 'text-emerald-400',
            titleColor: 'text-emerald-300',
            accentGradient: 'from-emerald-500/20 via-teal-500/10 to-transparent',
            buttonBg: 'bg-emerald-500/20 hover:bg-emerald-500/30',
            buttonBorder: 'border-emerald-500/40',
        },
        info: {
            icon: Info,
            glowColor: 'rgba(44, 109, 121, 0.5)',
            solidColor: 'rgb(44, 109, 121)',
            borderColor: 'border-teal-500/30',
            iconBg: 'bg-teal-500/20',
            iconColor: 'text-teal-400',
            titleColor: 'text-teal-300',
            accentGradient: 'from-teal-500/20 via-cyan-500/10 to-transparent',
            buttonBg: 'bg-teal-500/20 hover:bg-teal-500/30',
            buttonBorder: 'border-teal-500/40',
        },
    };

    const config = alertConfig[alert.type as keyof typeof alertConfig] || alertConfig.info;
    const IconComponent = config.icon;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    return (
        <>
            {/* ========== FLOATING BELL BUTTON ========== */}
            {!isModalOpen && (
                <button
                    onClick={handleOpenModal}
                    onMouseEnter={() => setIsButtonHovered(true)}
                    onMouseLeave={() => setIsButtonHovered(false)}
                    className={cn(
                        "fixed z-[9990] bottom-6 right-6",
                        "p-4 rounded-2xl",
                        "bg-slate-900/90 backdrop-blur-xl",
                        "border",
                        config.buttonBorder,
                        "shadow-2xl shadow-black/50",
                        "transition-all duration-500 ease-out",
                        "hover:scale-110 active:scale-95",
                        "group cursor-pointer"
                    )}
                    style={{
                        boxShadow: isButtonHovered
                            ? `0 0 30px ${config.glowColor}, 0 20px 40px rgba(0,0,0,0.4)`
                            : `0 0 15px ${config.glowColor}, 0 10px 30px rgba(0,0,0,0.3)`,
                    }}
                    aria-label="Visualizza avviso"
                >
                    {/* Pulse ring animation */}
                    {showPulse && (
                        <>
                            <span
                                className="absolute inset-0 rounded-2xl animate-ping opacity-30"
                                style={{ backgroundColor: config.solidColor }}
                            />
                            <span
                                className="absolute inset-0 rounded-2xl animate-pulse opacity-20"
                                style={{
                                    backgroundColor: config.solidColor,
                                    animationDelay: '0.5s'
                                }}
                            />
                        </>
                    )}

                    {/* Glow effect */}
                    <div
                        className={cn(
                            "absolute inset-0 rounded-2xl blur-xl transition-opacity duration-300",
                            isButtonHovered ? "opacity-60" : "opacity-30"
                        )}
                        style={{ background: config.glowColor }}
                    />

                    {/* Icon */}
                    <Megaphone
                        className={cn(
                            "relative h-6 w-6 transition-all duration-300",
                            config.iconColor,
                            "group-hover:rotate-12"
                        )}
                        style={{
                            filter: `drop-shadow(0 0 8px ${config.glowColor})`,
                        }}
                    />

                    {/* Notification dot */}
                    <span
                        className={cn(
                            "absolute -top-1 -right-1 w-3 h-3 rounded-full",
                            "animate-pulse"
                        )}
                        style={{ backgroundColor: config.solidColor }}
                    />

                    {/* Tooltip on hover */}
                    <span
                        className={cn(
                            "absolute right-full mr-3 top-1/2 -translate-y-1/2",
                            "px-3 py-2 rounded-lg",
                            "bg-slate-800/95 backdrop-blur-sm",
                            "border border-white/10",
                            "text-sm text-white whitespace-nowrap",
                            "opacity-0 group-hover:opacity-100",
                            "translate-x-2 group-hover:translate-x-0",
                            "transition-all duration-300",
                            "pointer-events-none"
                        )}
                    >
                        {alert.title || 'Avviso importante'}
                    </span>
                </button>
            )}

            {/* ========== MODAL ========== */}
            {isModalOpen && createPortal(
                <>
                    {/* Backdrop overlay */}
                    <div
                        className={cn(
                            "fixed inset-0 z-[9998] transition-all duration-500",
                            "bg-black/60 backdrop-blur-sm",
                            isExiting ? "opacity-0" : "opacity-100"
                        )}
                        onClick={handleBackdropClick}
                    />

                    {/* Alert Card */}
                    <div
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                    <div
                        className={cn(
                            "w-full max-w-lg pointer-events-auto",
                            "transition-all duration-500 ease-out",
                            isExiting
                                ? "opacity-0 scale-90 translate-y-8"
                                : "opacity-100 scale-100 translate-y-0"
                        )}
                        style={{
                            transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Glow effect behind card */}
                        <div
                            className="absolute inset-0 -z-10 blur-3xl opacity-60 animate-pulse"
                            style={{
                                background: `radial-gradient(ellipse at center, ${config.glowColor} 0%, transparent 70%)`,
                                transform: 'scale(1.2)',
                            }}
                        />

                        {/* Main card */}
                        <div
                            className={cn(
                                "relative overflow-hidden rounded-2xl",
                                "bg-slate-900/80 backdrop-blur-xl",
                                "border",
                                config.borderColor,
                                "shadow-2xl shadow-black/50"
                            )}
                        >
                            {/* Top accent gradient */}
                            <div
                                className={cn(
                                    "absolute inset-x-0 top-0 h-32 pointer-events-none",
                                    "bg-gradient-to-b",
                                    config.accentGradient
                                )}
                            />

                            {/* Animated particles/sparkles effect */}
                            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                                <div
                                    className="absolute -top-4 -right-4 w-24 h-24 opacity-20"
                                    style={{
                                        background: `radial-gradient(circle, ${config.glowColor} 0%, transparent 70%)`,
                                        animation: isMobile ? undefined : 'float 6s ease-in-out infinite',
                                    }}
                                />
                                <div
                                    className="absolute -bottom-8 -left-8 w-32 h-32 opacity-15"
                                    style={{
                                        background: `radial-gradient(circle, ${config.glowColor} 0%, transparent 70%)`,
                                        animation: isMobile ? undefined : 'float 6s ease-in-out infinite',
                                    }}
                                />
                            </div>

                            {/* Close button */}
                            {alert.canClose && (
                                <button
                                    onClick={handleClose}
                                    className={cn(
                                        "absolute right-4 top-4 z-10",
                                        "p-2 rounded-full",
                                        "bg-white/5 hover:bg-white/10",
                                        "border border-white/10 hover:border-white/20",
                                        "text-slate-400 hover:text-white",
                                        "transition-all duration-300",
                                        "hover:scale-110 active:scale-95",
                                        "group"
                                    )}
                                    aria-label="Chiudi"
                                >
                                    <X className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
                                </button>
                            )}

                            {/* Content */}
                            <div className="relative p-8 pt-6">
                                {/* Icon */}
                                <div className="flex justify-center mb-6">
                                    <div
                                        className={cn(
                                            "relative p-4 rounded-2xl",
                                            config.iconBg,
                                            "border border-white/10",
                                            "shadow-lg"
                                        )}
                                    >
                                        {/* Icon glow */}
                                        <div
                                            className="absolute inset-0 rounded-2xl blur-xl opacity-50"
                                            style={{ background: config.glowColor }}
                                        />
                                        <IconComponent
                                            className={cn(
                                                "relative h-8 w-8",
                                                config.iconColor,
                                                "drop-shadow-lg"
                                            )}
                                            style={{
                                                filter: `drop-shadow(0 0 8px ${config.glowColor})`,
                                            }}
                                        />

                                        {/* Sparkle decoration */}
                                        <Sparkles
                                            className={cn(
                                                "absolute -top-1 -right-1 h-3 w-3",
                                                config.iconColor,
                                                "opacity-60"
                                            )}
                                            style={{
                                                animation: isMobile ? undefined : 'sparkle 1.5s ease-in-out infinite',
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Title */}
                                <h2
                                    className={cn(
                                        "text-center font-montserrat font-bold text-2xl mb-3",
                                        config.titleColor,
                                        "tracking-tight"
                                    )}
                                    style={{
                                        textShadow: `0 0 30px ${config.glowColor}`,
                                    }}
                                >
                                    {alert.title || 'Avviso di Sistema'}
                                </h2>

                                {/* Message - supports HTML from rich text editor */}
                                <div
                                    className={cn(
                                        "text-center text-slate-300 text-lg leading-relaxed",
                                        "prose prose-lg prose-invert max-w-none",
                                        "prose-p:my-2 prose-a:text-teal-400 prose-a:no-underline hover:prose-a:underline",
                                        "prose-strong:text-white prose-em:text-slate-200",
                                        "max-h-[50vh] overflow-y-auto md:max-h-none md:overflow-y-visible"
                                    )}
                                    dangerouslySetInnerHTML={{ __html: alert.message }}
                                />

                                {/* Action button */}
                                {alert.canClose && (
                                    <div className="mt-8 flex justify-center">
                                        <button
                                            onClick={handleClose}
                                            className={cn(
                                                "group relative px-8 py-3",
                                                "bg-white/10 hover:bg-white/15",
                                                "border border-white/20 hover:border-white/30",
                                                "rounded-xl",
                                                "text-white font-medium text-lg",
                                                "transition-all duration-300",
                                                "hover:scale-105 active:scale-95",
                                                "overflow-hidden"
                                            )}
                                        >
                                            {/* Button glow on hover */}
                                            <span
                                                className={cn(
                                                    "absolute inset-0 opacity-0 group-hover:opacity-100",
                                                    "transition-opacity duration-300",
                                                    "bg-gradient-to-r",
                                                    config.accentGradient
                                                )}
                                            />
                                            <span className="relative">Ho capito</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Bottom decorative line */}
                            <div
                                className="h-1 w-full"
                                style={{
                                    background: `linear-gradient(90deg, transparent, ${config.glowColor}, transparent)`,
                                }}
                            />
                        </div>
                    </div>
                    </div>
                </>,
                document.body
            )}

            {/* Keyframe animations */}
            <style>{`
                @keyframes float {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    50% { transform: translate(-10px, 10px) scale(1.1); }
                }
                @keyframes sparkle {
                    0%, 100% { opacity: 0.6; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.2); }
                }
            `}</style>
        </>
    );
}

// Re-export per retrocompatibilità
export { SiteAlertSystem as SiteAlert };
